<template>
  <a-modal
    v-model:open="visible"
    title="高级检索"
    width="920px"
    ok-text="检索"
    cancel-text="取消"
    :confirm-loading="loading"
    :destroy-on-close="false"
    @ok="handleSubmit"
    @cancel="handleCancel"
  >
    <a-spin :spinning="loading">
      <div class="advanced-search-builder">
        <div class="advanced-search-builder-head">
          <span>检索条件</span>
          <div class="advanced-search-toolbar">
            <a-button size="small" :disabled="!searchConfig || !canAddRow" @click="addRow">添加条件</a-button>
            <a-button size="small" :disabled="!searchConfig" @click="resetRows">重置</a-button>
          </div>
        </div>
        <a-table :data-source="rows" :columns="columns" :pagination="false" row-key="rowKey" size="small">
          <template #bodyCell="{ column, record, index }">
            <template v-if="column.key === 'field'">
              <a-select
                v-model:value="record.field"
                style="width: 100%"
                :dropdown-match-select-width="false"
                popup-class-name="advanced-search-select-dropdown"
                @change="onFieldChange(record)"
              >
                <a-select-option v-for="f in availableFields(record)" :key="f.field" :value="f.field">
                  {{ f.label }}
                </a-select-option>
              </a-select>
            </template>
            <template v-else-if="column.key === 'clause'">
              <a-select
                v-model:value="record.clause"
                style="width: 100%"
                :dropdown-match-select-width="false"
                popup-class-name="advanced-search-select-dropdown"
              >
                <a-select-option v-for="c in clauseOptions(record.field)" :key="c.value" :value="c.value">
                  {{ c.label }}
                </a-select-option>
              </a-select>
            </template>
            <template v-else-if="column.key === 'match_mode'">
              <a-select
                v-model:value="record.match_mode"
                style="width: 100%"
                :dropdown-match-select-width="false"
                popup-class-name="advanced-search-select-dropdown"
                @change="onMatchModeChange(record)"
              >
                <a-select-option v-for="m in matchModeOptions(record.field)" :key="m.value" :value="m.value">
                  {{ m.label }}
                </a-select-option>
              </a-select>
            </template>
            <template v-else-if="column.key === 'value'">
              <div v-if="isDateRangeRow(record)" class="advanced-search-date-range">
                <a-date-picker
                  class="advanced-search-date-picker"
                  :value="record.value || null"
                  value-format="YYYY-MM-DD"
                  placeholder="开始日期"
                  @change="(_, dateString) => { record.value = dateString || '' }"
                />
                <span class="advanced-search-date-sep">至</span>
                <a-date-picker
                  class="advanced-search-date-picker"
                  :value="record.valueEnd || null"
                  value-format="YYYY-MM-DD"
                  placeholder="结束日期"
                  @change="(_, dateString) => { record.valueEnd = dateString || '' }"
                />
              </div>
              <a-date-picker
                v-else-if="isDateFieldRow(record)"
                class="advanced-search-date-picker"
                :value="record.value || null"
                value-format="YYYY-MM-DD"
                placeholder="选择日期"
                :disabled="record.match_mode === 'exists'"
                @change="(_, dateString) => { record.value = dateString || '' }"
              />
              <div v-else-if="isTreeFieldRow(record)" class="advanced-search-tree">
                <TreeFilter
                  :options="treeOptions(record.field)"
                  :model-value="Array.isArray(record.value) ? record.value : []"
                  @update:model-value="(v) => { record.value = Array.isArray(v) ? v : [] }"
                />
              </div>
              <a-select
                v-else-if="isSelectFieldRow(record)"
                v-model:value="record.value"
                style="width: 100%"
                allow-clear
                show-search
                option-filter-prop="label"
                :mode="isMultiSelectRow(record) ? 'multiple' : undefined"
                :max-tag-count="isMultiSelectRow(record) ? 2 : undefined"
                :placeholder="valuePlaceholder(record)"
                :disabled="record.match_mode === 'exists'"
                :dropdown-match-select-width="false"
                popup-class-name="advanced-search-select-dropdown"
                :options="selectOptions(record.field)"
              />
              <a-input
                v-else
                v-model:value="record.value"
                :placeholder="valuePlaceholder(record)"
                :disabled="record.match_mode === 'exists'"
              />
            </template>
            <template v-else-if="column.key === 'actions'">
              <a-button type="link" size="small" danger :disabled="rows.length <= 1" @click="removeRow(index)">
                删除
              </a-button>
            </template>
          </template>
        </a-table>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import { message } from 'ant-design-vue'
import { getAdvancedSearchConfig, getFilterOptions } from '@/api/filterApi'
import TreeFilter from '@/components/filters/TreeFilter.vue'
import {
  createDefaultRows,
  createEmptyRow,
  buildAdvancedSearchBody,
  getValuePlaceholder,
  getClauseOptions,
  getMatchModeOptions,
  onFieldChange as applyFieldChange,
  onMatchModeChange as applyMatchModeChange,
  isDateField,
  isDateRangeField,
  isSelectField,
  isTreeField,
  isMultiValueSelect,
  getFieldSelectOptions,
  getFieldTreeOptions,
  enrichAdvancedSearchConfigWithFilters,
} from '@/utils/advancedSearch'

let rowKeySeq = 0

export default {
  name: 'AdvancedSearchModal',
  components: {
    TreeFilter,
  },
  props: {
    open: { type: Boolean, default: false },
    labelGroup: { type: Number, required: true },
  },
  emits: ['update:open', 'submit'],
  data() {
    return {
      loading: false,
      searchConfig: null,
      loadedLabelGroup: null,
      rows: [],
      columns: [
        { title: '检索项', key: 'field', width: '24%' },
        { title: '逻辑', key: 'clause', width: 120 },
        { title: '匹配', key: 'match_mode', width: 140 },
        { title: '检索词', key: 'value' },
        { title: '', key: 'actions', width: 56 },
      ],
    }
  },
  computed: {
    visible: {
      get() { return this.open },
      set(v) { this.$emit('update:open', v) },
    },
    indexFields() {
      return this.searchConfig?.fields || []
    },
    canAddRow() {
      const used = new Set(this.rows.map((r) => r.field).filter(Boolean))
      return this.indexFields.some((f) => !used.has(f.field))
    },
  },
  watch: {
    open(val) {
      if (val) this.ensureReady()
    },
    labelGroup(newVal, oldVal) {
      if (newVal === oldVal) return
      this.clearDraft()
      if (this.open) this.ensureReady()
    },
  },
  methods: {
    availableFields(row) {
      const usedByOthers = new Set(
        this.rows
          .filter((r) => r !== row && r.field)
          .map((r) => r.field),
      )
      return this.indexFields.filter((f) => f.field === row.field || !usedByOthers.has(f.field))
    },
    valuePlaceholder(row) {
      return getValuePlaceholder(row, this.searchConfig)
    },
    clauseOptions(field) {
      return getClauseOptions(this.searchConfig, field)
    },
    matchModeOptions(field) {
      return getMatchModeOptions(this.searchConfig, field)
    },
    isDateRangeRow(row) {
      return row.match_mode === 'range' && isDateRangeField(this.searchConfig, row.field)
    },
    isDateFieldRow(row) {
      return row.match_mode !== 'exists' && isDateField(this.searchConfig, row.field)
    },
    isTreeFieldRow(row) {
      return row.match_mode !== 'exists' && isTreeField(this.searchConfig, row.field)
    },
    isSelectFieldRow(row) {
      return row.match_mode !== 'exists' && isSelectField(this.searchConfig, row.field)
    },
    isMultiSelectRow(row) {
      return isMultiValueSelect(this.searchConfig, row.field, row.match_mode)
    },
    selectOptions(field) {
      return getFieldSelectOptions(this.searchConfig, field)
    },
    treeOptions(field) {
      return getFieldTreeOptions(this.searchConfig, field)
    },
    async ensureReady() {
      const sameGroup = this.loadedLabelGroup === this.labelGroup && this.searchConfig
      if (sameGroup && this.rows.length) return
      await this.loadConfig({ resetRows: !this.rows.length || this.loadedLabelGroup !== this.labelGroup })
    },
    async loadConfig({ resetRows = true } = {}) {
      this.loading = true
      try {
        const [configRes, filterRes] = await Promise.all([
          getAdvancedSearchConfig(this.labelGroup),
          getFilterOptions(this.labelGroup).catch(() => null),
        ])
        const filters = filterRes?.errno === 0 ? (filterRes.data || []) : []
        this.searchConfig = enrichAdvancedSearchConfigWithFilters(configRes.data, filters)
        this.loadedLabelGroup = this.labelGroup
        if (resetRows || !this.rows.length) this.initRows()
      } catch (e) {
        this.searchConfig = null
        this.loadedLabelGroup = null
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    initRows() {
      const defaults = createDefaultRows(this.searchConfig, 3)
      this.rows = defaults.map((r) => ({ ...r, rowKey: ++rowKeySeq }))
      if (!this.rows.length && this.searchConfig) {
        this.rows = [{ ...createEmptyRow(this.searchConfig), rowKey: ++rowKeySeq }]
      }
      this.dedupeRowFields()
    },
    dedupeRowFields() {
      const used = new Set()
      const unused = () => this.indexFields.find((f) => !used.has(f.field))
      this.rows.forEach((row) => {
        if (row.field && !used.has(row.field)) {
          used.add(row.field)
          return
        }
        const next = unused()
        if (next) {
          row.field = next.field
          applyFieldChange(row, this.searchConfig)
          used.add(next.field)
        } else {
          row.field = undefined
        }
      })
      this.rows = this.rows.filter((r) => r.field)
      if (!this.rows.length && this.searchConfig) {
        this.rows = [{ ...createEmptyRow(this.searchConfig), rowKey: ++rowKeySeq }]
      }
    },
    onFieldChange(row) {
      applyFieldChange(row, this.searchConfig)
    },
    onMatchModeChange(row) {
      applyMatchModeChange(row, this.searchConfig)
    },
    addRow() {
      if (!this.searchConfig || !this.canAddRow) return
      const used = new Set(this.rows.map((r) => r.field).filter(Boolean))
      const nextField = this.indexFields.find((f) => !used.has(f.field))
      if (!nextField) return
      const row = { ...createEmptyRow(this.searchConfig), rowKey: ++rowKeySeq }
      row.field = nextField.field
      applyFieldChange(row, this.searchConfig)
      this.rows.push(row)
    },
    removeRow(index) {
      if (this.rows.length <= 1) return
      this.rows.splice(index, 1)
    },
    resetRows() {
      this.initRows()
    },
    clearDraft() {
      this.searchConfig = null
      this.loadedLabelGroup = null
      this.rows = []
    },
    handleCancel() {
      this.visible = false
    },
    handleSubmit() {
      if (!this.searchConfig) {
        message.warning('检索配置加载失败，请稍后重试')
        return
      }
      const body = buildAdvancedSearchBody(this.rows, {
        page: 1,
        limit: 20,
        config: this.searchConfig,
      })
      this.$emit('submit', body)
      this.visible = false
    },
  },
}
</script>

<style scoped>
.advanced-search-builder {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
}
.advanced-search-builder-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fafafa;
  border-bottom: 1px solid #d9d9d9;
  font-size: 14px;
  font-weight: 500;
}
.advanced-search-toolbar {
  display: flex;
  gap: 8px;
}
.advanced-search-date-range {
  display: flex;
  align-items: center;
  gap: 6px;
}
.advanced-search-date-sep {
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
}
.advanced-search-date-picker {
  width: 100%;
  min-width: 0;
}
.advanced-search-tree {
  width: 100%;
  min-width: 0;
}
.advanced-search-tree :deep(.tree-filter) {
  width: 100%;
}
</style>

<style>
.advanced-search-select-dropdown.ant-select-dropdown .ant-select-item-option-content {
  white-space: nowrap;
}
</style>
